import { Clock, FileText, MapPin, Eye, Network } from 'lucide-react';
import { currentUserId, isCaseArtifact, readHideArtifacts, recentCaseIdsFrom } from '../utils/caseArtifacts';
import { useDashboardCache } from '../utils/dashboardCache';
import { DashboardIcon } from './dashboard/DashboardIcon';
import { DashboardSurface } from './dashboard/DashboardSurface';
import type { DashboardIconTone } from './dashboard/dashboardIconConfig';

interface RecentlyViewedItem {
  resource_type: string;
  resource_id: string;
  resource_data: {
    label?: string;
    title?: string;
    address?: string;
    risk_score?: number;
    entity_name?: string;
  };
  view_count: number;
  last_viewed_at: string;
}

const RESOURCE_ICON: Record<string, { icon: typeof MapPin; tone: DashboardIconTone }> = {
  address: { icon: MapPin, tone: 'info' },
  case: { icon: FileText, tone: 'success' },
  investigation: { icon: Network, tone: 'accent' },
};

export function RecentlyViewed({ maxHeightClass = 'max-h-64' }: { maxHeightClass?: string }) {
  const cache = useDashboardCache();
  const raw = cache.get<RecentlyViewedItem[]>('recentlyViewed');
  const loading = cache.loading('recentlyViewed') && raw === undefined;
  // Same rule as the Cases list: off unless the officer turned it on, and a case
  // viewed in the last 30 days (every row here was viewed by this user) stays.
  const hideArtifacts = readHideArtifacts();
  const recentIds = recentCaseIdsFrom(raw || []);
  const items = (raw || []).filter((item: RecentlyViewedItem) => {
    if (!hideArtifacts || item.resource_type !== 'case') return true;
    return !isCaseArtifact(
      { id: item.resource_id, title: item.resource_data?.title || item.resource_data?.label },
      { currentUserId: currentUserId(), recentCaseIds: recentIds },
    );
  });

  const getIcon = (type: string) => {
    const config = RESOURCE_ICON[type] ?? { icon: Eye, tone: 'neutral' as DashboardIconTone };
    return <DashboardIcon variant="tile" size="sm" tone={config.tone} icon={config.icon} />;
  };

  const getNavigationUrl = (item: RecentlyViewedItem) => {
    if (item.resource_type === 'address') {
      return `/workbench?query=${encodeURIComponent(item.resource_id)}`;
    } else if (item.resource_type === 'case') {
      return `/cases?id=${item.resource_id}`;
    } else if (item.resource_type === 'investigation') {
      const caseId = (item.resource_data as { case_id?: string })?.case_id;
      if (caseId) {
        return `/workbench?case_id=${caseId}&investigation_id=${item.resource_id}`;
      }
      return `/workbench?investigation_id=${item.resource_id}`;
    }
    return '#';
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <DashboardSurface className="p-4">
        <h3 className="text-sm font-semibold mb-4">Recently Viewed</h3>
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        </div>
      </DashboardSurface>
    );
  }

  return (
    <DashboardSurface className="p-4 h-full">
      <div className="border-b border-border/60 pb-2 mb-2">
        <h3 className="text-sm font-semibold text-foreground">Recently Viewed</h3>
        <p className="text-xs text-muted-foreground">Quick access to your investigations</p>
      </div>

      {items.length === 0 ? (
        <div className="p-6 text-center text-muted-foreground">
          <DashboardIcon variant="tile" size="lg" tone="neutral" icon={Eye} className="mx-auto mb-2 opacity-50" />
          <p>No recently viewed items</p>
        </div>
      ) : (
        <div className={`divide-y divide-border ${maxHeightClass} overflow-y-auto`}>
          {items.map((item, index) => (
            <a
              key={`${item.resource_type}-${item.resource_id}-${index}`}
              href={getNavigationUrl(item)}
              className="block p-4 hover:bg-secondary/20 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getIcon(item.resource_type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {item.resource_data.title ||
                          item.resource_data.label ||
                          item.resource_data.entity_name ||
                          `${item.resource_type} - ${item.resource_id.slice(0, 10)}...`}
                      </p>

                      {item.resource_data.address && (
                        <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">
                          {item.resource_data.address}
                        </p>
                      )}

                      {item.resource_data.risk_score !== undefined && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">Risk:</span>
                          <span className={`text-xs font-semibold ${item.resource_data.risk_score >= 0.7 ? 'text-red-500' :
                            item.resource_data.risk_score >= 0.4 ? 'text-yellow-500' :
                              'text-green-500'
                            }`}>
                            {(item.resource_data.risk_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <DashboardIcon variant="inline" tone="neutral" icon={Eye} />
                          <span>{item.view_count} {item.view_count === 1 ? 'view' : 'views'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DashboardIcon variant="inline" tone="neutral" icon={Clock} />
                          <span>{formatTimeAgo(item.last_viewed_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </DashboardSurface>
  );
}
